// Special Use Airspace (SUA) layer family.
//
// FAA Special Use Airspace — MOAs, Restricted, Warning, Prohibited, and
// Alert areas. One GeoJSON source drives three layers: a hatch-pattern
// fill (type-specific pattern from registerHatchPatterns()), a
// type-coded color border line, and a symbol label at zoom ≥ 7. All
// layers are mounted with visibility: 'none' (lazy toggle).
//
// Hatch patterns are registered globally by registerHatchPatterns()
// which is still called from addOverlays.ts before the first mount
// of any overlay layers. The patterns are available in the map style
// by the time addSuaLayers() runs.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 1 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addSuaLayers(map: MlMap): void {
  if (!map.getSource('sua')) {
    map.addSource('sua', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'sua-fill',
      type: 'fill',
      source: 'sua',
      layout: { visibility: 'none' },
      paint: {
        'fill-pattern': [
          'match', ['get', 'TYPE_CODE'],
          'P', 'hatch-prohibited',
          'R', 'hatch-restricted',
          'W', 'hatch-warning',
          'A', 'hatch-alert',
          'M', 'hatch-moa',
          /* default */ 'hatch-other',
        ],
        'fill-opacity': 0.85,
      },
    });
    map.addLayer({
      id: 'sua-line',
      type: 'line',
      source: 'sua',
      layout: { visibility: 'none' },
      paint: {
        'line-color': [
          'match', ['get', 'TYPE_CODE'],
          'P', '#dc2626', 'R', '#ea580c', 'W', '#ca8a04',
          'A', '#a16207', 'M', '#7c3aed',
          /* default */ '#fbbf24',
        ],
        'line-width': 1.5,
      },
    });
    map.addLayer({
      id: 'sua-label',
      type: 'symbol',
      source: 'sua',
      minzoom: 7,
      layout: {
        visibility: 'none',
        'text-field': ['get', 'NAME'],
        'text-size': 10,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#fde68a',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
    });
  }
}

registerLayerFamily({
  id: 'sua',
  label: 'Special Use Airspace',
  sourceId: 'sua',
  layerIds: ['sua-fill', 'sua-line', 'sua-label'],
  mount: (map) => addSuaLayers(map),
  visibilitySelector: (l) => l.sua,
  applyState: (map) => {
    if (!map.getLayer('sua-fill')) return;
    const show = useLayerVisibilityStore.getState().layers.sua;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('sua-fill', 'visibility', v);
    map.setLayoutProperty('sua-line', 'visibility', v);
    map.setLayoutProperty('sua-label', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('sua');
    if (!spec || isOverlayLoaded('sua')) return;
    markOverlayLoaded('sua');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!isOverlayLoaded('sua')) return;
    const spec = OVERLAY_BY_ID.get('sua');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
